// Fence = complement of the band so MUST_NOT_HAVE keeps the band on its shelves.
import type {
  AcceptedRestriction,
  RestrictionCell,
  RestrictionPlacement,
  StudioRunGeometry,
} from "./studioTypes";

export const shelfUniverseOf = (run: StudioRunGeometry): number[] =>
  [...new Set(run.shelves.map((shelf) => shelf.rank))].sort((a, b) => a - b);

export const baysOfRun = (run: StudioRunGeometry): number[] =>
  Array.from({ length: Math.max(run.bays, 0) }, (_, index) => index + 1);

/** Complement cells over the whole run; empty complement is null so we never emit a vacuous fence. */
export const fencePlacementFor = (
  band: RestrictionPlacement | null,
  shelfUniverse: readonly number[],
  bays: readonly number[],
): RestrictionPlacement | null => {
  if (!band || band.cells.length === 0) return null;
  if (bays.length === 0) return null;

  const occupied = new Set(band.cells.map((cell) => cell.shelf));
  const outside = [...new Set(shelfUniverse)]
    .filter((shelf) => !occupied.has(shelf))
    .sort((a, b) => a - b);
  if (outside.length === 0) return null;

  const orderedBays = [...new Set(bays)].sort((a, b) => a - b);
  const cells: RestrictionCell[] = outside.map((shelf) => ({ shelf, bays: orderedBays }));
  return { cells };
};

export const fenceRestrictionFrom = (
  band: AcceptedRestriction,
  shelfUniverse: readonly number[],
  bays: readonly number[],
): AcceptedRestriction | null => {
  const placement = fencePlacementFor(band.placement, shelfUniverse, bays);
  if (!placement) return null;

  const kept = band.placement?.cells.map((cell) => cell.shelf) ?? [];
  const variant = band.cdtPath[band.cdtPath.length - 1] ?? "the band";
  return {
    ...band,
    condition: "MUST_NOT_HAVE",
    placement,
    text: `${variant} is kept to shelf ${kept.join(", ")} — it must not appear on shelf ${placement.cells
      .map((cell) => cell.shelf)
      .join(", ")}.`,
  };
};
